import React from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Clock, TrendingUp, AlertCircle, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BillingItem {
  id: string;
  description: string;
  amount: number;
  type?: 'tuition' | 'fee' | 'discount' | 'late_fee';
}

interface BillingData {
  totalBalance: number;
  dueDate: string;
  status: 'current' | 'overdue' | 'paid';
  items: BillingItem[];
  paymentHistory?: {
    lastPayment?: {
      amount: number;
      date: string;
    };
  };
}

interface CanvaBillingCardProps {
  billingData: BillingData;
  onPayNow?: () => void;
  onViewHistory?: () => void;
  className?: string;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'current':
      return { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-400' };
    case 'overdue':
      return { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-400' };
    case 'paid':
      return { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-400' };
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-800', dot: 'bg-gray-400' };
  }
};

export const CanvaBillingCard: React.FC<CanvaBillingCardProps> = ({
  billingData,
  onPayNow,
  onViewHistory,
  className
}) => {
  const statusColor = getStatusColor(billingData.status);
  const isOverdue = billingData.status === 'overdue';
  
  return (
    <div className={cn("mb-10", className)}>
      <motion.h2 
        className="text-2xl font-bold text-gray-900 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Billing Overview
      </motion.h2>
      
      <motion.div
        className="canva-billing-card canva-card-hover"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        {/* Decorative background */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-50 to-green-100 rounded-bl-3xl opacity-60" />
        
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 relative z-10">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Account Balance</h3>
            <p className="text-gray-600">Current balance and recent activity</p>
          </div>
          
          <div className="text-left lg:text-right mt-4 lg:mt-0">
            <motion.p 
              className="text-4xl font-bold text-gray-900 mb-1"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              {formatCurrency(billingData.totalBalance)}
            </motion.p>
            <div className="flex items-center gap-2 lg:justify-end">
              <span 
                className={cn(
                  "w-2 h-2 rounded-full canva-pulse-dot",
                  isOverdue ? "bg-red-400" : "bg-orange-400"
                )}
              />
              <p className={cn(
                "font-semibold",
                isOverdue ? "text-red-600" : "text-orange-600"
              )}>
                {isOverdue ? 'Overdue' : `Due ${formatDate(billingData.dueDate)}`}
              </p>
            </div>
          </div>
        </div>

        {/* Billing Items */}
        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <div className="space-y-3">
            {billingData.items.map((item, index) => (
              <motion.div
                key={item.id}
                className="flex items-center justify-between"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.1, duration: 0.4 }}
              >
                <div className="flex items-center gap-3">
                  <span className="text-gray-600 font-medium">{item.description}</span>
                  {item.type === 'late_fee' && (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  )}
                </div>
                <span className={cn(
                  "font-bold",
                  item.type === 'discount' ? 'text-green-600' : 
                  item.type === 'late_fee' ? 'text-red-600' : 'text-gray-900'
                )}>
                  {item.type === 'discount' ? '-' : ''}{formatCurrency(Math.abs(item.amount))}
                </span>
              </motion.div>
            ))}
          </div>
          
          {/* Payment History Summary */}
          {billingData.paymentHistory?.lastPayment && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Last Payment:</span>
                <span className="text-gray-700 font-medium">
                  {formatCurrency(billingData.paymentHistory.lastPayment.amount)} on{' '}
                  {formatDate(billingData.paymentHistory.lastPayment.date)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <motion.div 
          className="flex flex-col sm:flex-row gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <motion.button
            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-6 rounded-xl font-bold shadow-lg transition-all relative overflow-hidden group"
            onClick={onPayNow}
            whileHover={{ 
              scale: 1.02, 
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' 
            }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full transform translate-x-6 -translate-y-6 group-hover:scale-150 transition-transform duration-300" />
            <div className="relative z-10 flex items-center justify-center gap-2">
              <CreditCard className="w-5 h-5" />
              Pay Now
            </div>
          </motion.button>
          
          <motion.button
            className="flex-1 bg-gray-100 text-gray-700 py-4 px-6 rounded-xl font-bold border border-gray-200 hover:bg-gray-200 transition-all"
            onClick={onViewHistory}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center justify-center gap-2">
              <ExternalLink className="w-4 h-4" />
              View History
            </div>
          </motion.button>
        </motion.div>

        {/* Status Badge */}
        <motion.div 
          className="absolute top-6 right-6 z-20"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.8, duration: 0.5, type: "spring", stiffness: 200 }}
        >
          <span className={cn(
            "inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border",
            statusColor.bg,
            statusColor.text,
            "border-current border-opacity-20"
          )}>
            <span className={cn("w-2 h-2 rounded-full mr-2", statusColor.dot)} />
            {billingData.status.charAt(0).toUpperCase() + billingData.status.slice(1)}
          </span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default CanvaBillingCard;